import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TYPOGRAPHY } from 'shared/design-system/settings/variables/Typography';
import RadioGroupMui from '@mui/material/RadioGroup';
import FormControlLabelMui from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import styled from 'styled-components';
import { Radio } from '../styles';
import isObject from 'lodash/isObject';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';

export const RadioGroup = ({
  options,
  value,
  onChange,
  renderLabel,
  style = undefined,
  subTitle = '',
  extraData = undefined,
  className = undefined,
  onClick = () => { /* noop */ },
  isOther = false,
}) => {
  return (
    <RadioGroupRoot
      aria-label={subTitle}
      name={subTitle}
      value={isOther ? 'Other' : value}
      {...{ onChange, style, className, onClick }}
    >
      {options.map((label, idx) => {
        if (extraData) {
          return (
            <Tooltip
              title={<RadioOptionDetails data={extraData[idx]} />}
              placement="right"
              arrow
            >
              <RadioWithLabel key={label} value={label} label={label} />
            </Tooltip>
          );
        } else if (isObject(label)) {
          const { response, responseValue } = label;

          const option = responseValue ?? response;

          return (
            <RadioWithLabel
              key={option}
              value={option}
              label={option}
              checked={option === value}
            />
          );
        } else {
          const text = renderLabel ? renderLabel(idx) : label;
          return <RadioWithLabel key={label} value={label} label={text} />;
        }
      })}
    </RadioGroupRoot>
  );
};

const RadioGroupRoot = styled(RadioGroupMui)`
  max-width: 320px;
  margin: 0 auto;
`;
export const RadioWithLabel = styled(FormControlLabelMui).attrs(
  ({ checked }) => ({
    control: <Radio {...{ checked }} />,
  }),
)`
  margin-bottom: ${SPACERS.tiny};
  .MuiFormControlLabel-label {
    color: ${COLORS.blackBase};
    font-size: ${TYPOGRAPHY.bodyBase};
  }
`;
const DetailsContainer = styled.div`
  min-width: 250px;
  height: auto;
  padding: 10px;
  padding-top: 0px;
  .description {
    padding: 16px 0;
    border-bottom: 1px solid ${COLORS.greyBase};
    margin-bottom: ${SPACERS.small};
  }
  .label {
    margin-bottom: ${SPACERS.tiny};
  }

  ul {
    margin: 0;
    padding-left: 0px;
    li {
      display: flex;
      align-items: flex-start;
      gap: ${SPACERS.tiny};
      padding-left: 0px;
      list-style: none;
      margin-bottom: ${SPACERS.tiny};
    }
    svg {
      margin-top: 3px;
      flex-shrink: 0;
    }
    .service-name {
      margin: 0;
    }
  }

  ul.matching-indicators {
    display: flex;
    flex-direction: column;
    margin: 8px 0 2px 2px;
    padding: 0 0 0 16px;
    border-left: 1.5px solid ${COLORS.greyLight};
    li {
      position: relative;
      margin-bottom: 0;
      align-items: stretch;
      padding: 6px 0;
      gap: 0;
      &::before {
        content: '';
        position: absolute;
        left: -16px;
        top: 14px;
        width: 11px;
        height: 1.5px;
        background-color: ${COLORS.greyLight};
      }
      & + li {
        border-top: 1px solid ${COLORS.greyLightest};
      }
      > div {
        display: flex;
        flex-direction: column;
        gap: 2px;
        width: 100%;
      }
      .indicator-name {
        color: ${COLORS.greyDarkest};
        font-weight: 600;
        margin: 0;
        line-height: 1.3;
      }
      .indicator-definition {
        color: ${COLORS.greyDark};
        margin: 0;
        line-height: 1.35;
      }
    }
  }
`;

const highlightMatch = (text, term) => {
  if (!term || !text) return text;
  const safe = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${safe})`, 'ig'));
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <mark key={i} className="search-highlight">
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

export const RadioOptionDetails = ({
  data: { description, name, services },
  highlight = '',
}) => (
  <DetailsContainer>
    <TrueImpactText variant="body" className="description">
      {highlightMatch(description, highlight)}
    </TrueImpactText>
    {services && (
      <TrueImpactText variant="heading-label" className="label">
        Impact Model templates:
      </TrueImpactText>
    )}
    {services && (
      <ul>
        {services.map(row => {
          const term = String(highlight).trim().toLowerCase();
          const matchingIndicators = term
            ? (row.indicators || []).filter(
                ind =>
                  ind.name?.toLowerCase().includes(term) ||
                  ind.definition?.toLowerCase().includes(term),
              )
            : [];
          return (
            <li key={row.dbId ?? row.name}>
              <FontAwesomeIcon
                icon={faCircleCheck}
                color="#373d3f"
              ></FontAwesomeIcon>
              <div>
                <TrueImpactText variant="body" className="service-name">
                  {highlightMatch(row.name, highlight)}
                </TrueImpactText>
                {matchingIndicators.length > 0 && (
                  <ul className="matching-indicators">
                    {matchingIndicators.map(ind => {
                      const nameMatches = ind.name
                        ?.toLowerCase()
                        .includes(term);
                      return (
                        <li key={ind.id ?? ind.name}>
                          <div>
                            <TrueImpactText
                              variant="body-small"
                              className="indicator-name"
                            >
                              {highlightMatch(ind.name, highlight)}
                            </TrueImpactText>
                            {!nameMatches && ind.definition && (
                              <TrueImpactText
                                variant="body-small-italic"
                                className="indicator-definition"
                              >
                                {highlightMatch(ind.definition, highlight)}
                              </TrueImpactText>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    )}
  </DetailsContainer>
);
